import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

export interface ProjectTemplate {
  id: string;
  name: string;
  templateDescription?: string;
  description?: string;
  priority?: string;
  type?: string;
  location?: string;
  unit?: string;
  currencyCode?: string;
  clients?: string[];
  tags?: string[];
  isFavorite: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface TemplateContextType {
  templates: ProjectTemplate[];
  addTemplate: (template: Omit<ProjectTemplate, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateTemplate: (id: string, updates: Partial<ProjectTemplate>) => void;
  deleteTemplate: (id: string) => void;
  toggleFavorite: (id: string) => void;
  getTemplate: (id: string) => ProjectTemplate | undefined;
}

const TemplateContext = createContext<TemplateContextType | undefined>(undefined);

// Initial mock templates
const initialMockTemplates: ProjectTemplate[] = [
  {
    id: '1',
    name: 'Software Development Project',
    templateDescription: 'Standard template for software development projects with phases and milestones',
    description: 'A comprehensive software development project with agile methodology',
    priority: 'High',
    type: 'Software Development',
    location: 'Remote',
    unit: 'Days',
    currencyCode: 'USD',
    clients: [],
    tags: ['software', 'agile', 'development'],
    isFavorite: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
  },
  {
    id: '2',
    name: 'Marketing Campaign',
    templateDescription: 'Template for marketing campaigns with budget tracking and deliverables',
    description: 'Marketing campaign with social media and content strategy',
    priority: 'Medium',
    type: 'Marketing',
    location: 'Hybrid',
    unit: 'Weeks',
    currencyCode: 'USD',
    clients: [],
    tags: ['marketing', 'campaign', 'social-media'],
    isFavorite: true,
    createdAt: new Date('2024-01-02'),
    updatedAt: new Date('2024-01-02'),
  },
  {
    id: '3',
    name: 'Infrastructure Upgrade',
    templateDescription: 'IT infrastructure project template with resource allocation',
    description: 'Infrastructure modernization and cloud migration project',
    priority: 'High',
    type: 'IT Infrastructure',
    location: 'On-site',
    unit: 'Months',
    currencyCode: 'USD',
    clients: [],
    tags: ['infrastructure', 'cloud', 'migration'],
    isFavorite: true,
    createdAt: new Date('2024-01-03'),
    updatedAt: new Date('2024-01-03'),
  },
  {
    id: '4',
    name: 'Product Launch',
    templateDescription: 'End-to-end product launch template with cross-functional tasks',
    description: 'Complete product launch with go-to-market strategy',
    priority: 'High',
    type: 'Product Launch',
    location: 'Hybrid',
    unit: 'Weeks',
    currencyCode: 'USD',
    clients: [],
    tags: ['product', 'launch', 'gtm'],
    isFavorite: false,
    createdAt: new Date('2024-01-04'),
    updatedAt: new Date('2024-01-04'),
  },
  {
    id: '5',
    name: 'Consulting Engagement',
    templateDescription: 'Professional services template with time tracking and billing',
    description: 'Consulting project with deliverables and milestones',
    priority: 'Medium',
    type: 'Consulting',
    location: 'Client Site',
    unit: 'Hours',
    currencyCode: 'USD',
    clients: [],
    tags: ['consulting', 'professional-services'],
    isFavorite: false,
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-05'),
  },
];

const STORAGE_KEY = 'project-templates';

// Helper to load templates from localStorage
const loadTemplatesFromStorage = (): ProjectTemplate[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert date strings back to Date objects
      return parsed.map((template: any) => ({
        ...template,
        createdAt: new Date(template.createdAt),
        updatedAt: new Date(template.updatedAt),
      }));
    }
  } catch (error) {
    console.error('Error loading templates from localStorage:', error);
  }
  // Return initial mock templates if nothing in storage
  return initialMockTemplates;
};

// Helper to save templates to localStorage
const saveTemplatesToStorage = (templates: ProjectTemplate[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch (error) {
    console.error('Error saving templates to localStorage:', error);
  }
};

export function TemplateProvider({ children }: { children: ReactNode }) {
  const [templates, setTemplates] = useState<ProjectTemplate[]>(() => loadTemplatesFromStorage());

  // Save to localStorage whenever templates change
  useEffect(() => {
    saveTemplatesToStorage(templates);
  }, [templates]);

  const addTemplate = useCallback((template: Omit<ProjectTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newTemplate: ProjectTemplate = {
      ...template,
      id: `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setTemplates(prev => [...prev, newTemplate]);
  }, []);

  const updateTemplate = useCallback((id: string, updates: Partial<ProjectTemplate>) => {
    setTemplates(prev =>
      prev.map(template =>
        template.id === id
          ? { ...template, ...updates, updatedAt: new Date() }
          : template
      )
    );
  }, []);

  const deleteTemplate = useCallback((id: string) => {
    setTemplates(prev => prev.filter(template => template.id !== id));
  }, []);

  const toggleFavorite = useCallback((id: string) => {
    setTemplates(prev =>
      prev.map(template =>
        template.id === id
          ? { ...template, isFavorite: !template.isFavorite, updatedAt: new Date() }
          : template
      )
    );
  }, []);

  const getTemplate = useCallback((id: string) => {
    return templates.find(template => template.id === id);
  }, [templates]);

  return (
    <TemplateContext.Provider
      value={{
        templates,
        addTemplate,
        updateTemplate,
        deleteTemplate,
        toggleFavorite,
        getTemplate,
      }}
    >
      {children}
    </TemplateContext.Provider>
  );
}

export function useTemplates() {
  const context = useContext(TemplateContext);
  if (context === undefined) {
    throw new Error('useTemplates must be used within a TemplateProvider');
  }
  return context;
}
